<template>
    <b-overlay :show="loading" variant="white" :rounded="true">
        <template v-slot:overlay>
            <div class="text-center">
                <b-spinner
                    label="Carregando..."
                    style="width: 3rem; height: 3rem"
                />
            </div>
        </template>
        <b-card class="p-0 m-0 shadow-sm" body-class="p-2">
            <chart-toolbar :chart="chart" />
            <div style="position: relative; height: 500px">
                <canvas ref="canvas" />
            </div>
        </b-card>
    </b-overlay>
</template>

<script>
import EasyTable from "easy-table";
import { Chart, defaultOptions, tickOptions } from "@/modules/chart.js";
import ChartToolbar from "@/modules/common/components/ChartToolbar";

export default {
    name: "ReservoirChart",
    components: {
        ChartToolbar
    },
    props: {
        includeWaterGates: {
            type: Boolean
        },
        loading: {
            type: Boolean
        }
    },
    data() {
        return {
            chart: null
        };
    },
    mounted() {
        this.waterGates = [];
        this.chart = new Chart(this.$refs.canvas, {
            type: "line",
            data: {
                datasets: []
            },
            options: {
                ...defaultOptions,
                tooltips: {
                    ...defaultOptions.tooltips,
                    footerFontFamily: '"Lucida Console", Monaco, monospace',
                    callbacks: {
                        ...defaultOptions.tooltips.callbacks,
                        afterFooter: tooltipItens => {
                            if (tooltipItens.length > 0) {
                                const { datasetIndex, index } = tooltipItens[0];
                                if (
                                    datasetIndex === 2 && // Vazão vertida
                                    Array.isArray(this.waterGates[index]) &&
                                    this.waterGates[index].some(
                                        item => item > 0
                                    )
                                ) {
                                    const table = new EasyTable();
                                    this.waterGates[index].forEach(
                                        (gap, index) => {
                                            if (gap > 0) {
                                                table.cell(
                                                    "Comporta",
                                                    index + 1,
                                                    EasyTable.number(0)
                                                );
                                                table.cell(
                                                    "Abertura (m)",
                                                    gap,
                                                    EasyTable.number(2)
                                                );
                                                table.newRow();
                                            }
                                        }
                                    );
                                    return `\n${table.toString()}`;
                                }
                            }
                            return "";
                        }
                    }
                },
                scales: {
                    xAxes: [
                        {
                            display: "auto",
                            type: "time",
                            time: {
                                tooltipFormat: "DD/MM/YYYY HH:mm",
                                displayFormats: {
                                    hour: "D MMM HH:mm",
                                    day: "D MMM"
                                },
                                minUnit: "hour"
                            },
                            ticks: {
                                maxTicksLimit: 9
                            }
                        }
                    ],
                    yAxes: [
                        {
                            type: "linear",
                            id: "left-axis",
                            display: "auto",
                            scaleLabel: {
                                display: true,
                                labelString: "Cota (m)",
                                fontStyle: "bold"
                            },
                            ticks: {
                                ...tickOptions
                            }
                        },
                        {
                            type: "linear",
                            id: "right-axis",
                            display: "auto",
                            position: "right",
                            scaleLabel: {
                                display: true,
                                labelString: "Vazão (m³/s)",
                                fontStyle: "bold"
                            },
                            ticks: {
                                ...tickOptions
                            }
                        }
                    ]
                }
            }
        });
    },
    beforeDestroy() {
        this.chart?.destroy();
    },
    methods: {
        update(points) {
            this.chart.resetZoom();

            const numPoints = points.length;
            this.waterGates = [];
            const datasets = [
                {
                    label: "Montante",
                    data: new Array(numPoints),
                    yAxisID: "left-axis",
                    fill: false,
                    pointRadius: 0,
                    lineTension: 0
                },
                {
                    label: "Jusante",
                    data: new Array(numPoints),
                    yAxisID: "left-axis",
                    fill: false,
                    pointRadius: 0,
                    lineTension: 0,
                    hidden: true
                }
            ];

            if (this.includeWaterGates) {
                this.waterGates = new Array(numPoints);
                datasets.push({
                    label: "Vazão vertida",
                    data: new Array(numPoints),
                    yAxisID: "right-axis",
                    fill: false,
                    pointRadius: 0,
                    lineTension: 0,
                    hidden: true
                });
            }

            for (let i = 0; i < numPoints; i++) {
                const {
                    quota_flux,
                    quota_downstream,
                    rate_flow,
                    water_gates,
                    epoch
                } = points[i];

                datasets[0].data[i] = {
                    x: epoch,
                    y: quota_flux
                };

                datasets[1].data[i] = {
                    x: epoch,
                    y: quota_downstream
                };

                if (this.includeWaterGates) {
                    this.waterGates[i] = water_gates;
                    datasets[2].data[i] = {
                        x: epoch,
                        y: rate_flow
                    };
                }
            }

            this.chart.data.datasets = datasets;
            this.chart.update();
        }
    }
};
</script>
